/**
 * User Switch Modal
 * Prompts the user when a magic link login would switch to a different account.
 * Shown when a user is already logged in and a magic link authenticates a different user.
 */

import React from 'react';
import { Modal, ActionButton } from '@turn-connect/ui';
import { theme } from '@turn-connect/ui';

interface UserSwitchUser {
  id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}

interface UserSwitchModalProps {
  isOpen: boolean;
  oldUser: UserSwitchUser;
  newUser: UserSwitchUser;
  onSwitch: () => void;
  onStay: () => void;
}

const getUserDisplayName = (user: UserSwitchUser): string => {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  if (user.first_name) {
    return user.first_name;
  }
  return user.email || 'Unknown User';
};

const getUserEmail = (user: UserSwitchUser): string => {
  return user.email || '';
};

const UserSwitchModal: React.FC<UserSwitchModalProps> = ({ isOpen, oldUser, newUser, onSwitch, onStay }) => {
  if (!isOpen) return null;

  const oldName = getUserDisplayName(oldUser);
  const oldEmail = getUserEmail(oldUser);
  const newName = getUserDisplayName(newUser);
  const newEmail = getUserEmail(newUser);

  return (
    <Modal
      title="Switch Account?"
      isOpen={isOpen}
      onClose={onStay}
      width="min(440px, 90vw)"
      showCloseButton={false}
      closeOnBackdropClick={false}
      footer={
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', width: '100%' }}>
          <ActionButton variant="cancel" onClick={onStay}>
            Stay as {oldName}
          </ActionButton>
          <ActionButton variant="primary" onClick={onSwitch}>
            Switch Account
          </ActionButton>
        </div>
      }
    >
      <div style={{ padding: `${theme.spacing[2]} 0` }}>
        <p style={{ color: theme.colors.text.secondary, margin: `0 0 ${theme.spacing[3]} 0`, lineHeight: 1.5 }}>
          You are currently logged in as a different user. Do you want to switch accounts?
        </p>

        {/* Current user */}
        <div
          style={{
            background: theme.colors.background.secondary,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: 8,
            padding: theme.spacing[3],
            marginBottom: theme.spacing[2],
          }}
        >
          <div style={{ color: theme.colors.text.tertiary, fontSize: theme.typography.fontSize.xs, marginBottom: 4 }}>
            Currently logged in as
          </div>
          <div style={{ color: theme.colors.text.primary, fontWeight: 600 }}>{oldName}</div>
          {oldEmail && <div style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm }}>{oldEmail}</div>}
        </div>

        {/* Arrow indicator */}
        <div style={{ textAlign: 'center', color: theme.colors.text.tertiary, margin: `${theme.spacing[1]} 0` }}>&#8595;</div>

        {/* New user */}
        <div
          style={{
            background: theme.colors.background.secondary,
            border: `1px solid ${theme.colors.status.info}`,
            borderRadius: 8,
            padding: theme.spacing[3],
          }}
        >
          <div style={{ color: theme.colors.text.tertiary, fontSize: theme.typography.fontSize.xs, marginBottom: 4 }}>Switch to</div>
          <div style={{ color: theme.colors.text.primary, fontWeight: 600 }}>{newName}</div>
          {newEmail && <div style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm }}>{newEmail}</div>}
        </div>
      </div>
    </Modal>
  );
};

export default UserSwitchModal;
